import React, { useMemo, useState } from 'react';
import Topbar from '@/layout/Topbar';
import Icon from '@/components/Icon';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { NAV_SECTIONS, isNavItemVisible } from '@/layout/navConfig';
import {
  getHiddenNavItems, setHiddenNavItems,
  getDefaultPage, setDefaultPage,
} from '@/lib/navPrefs';
import {
  isReportFilterPersistEnabled,
  setReportFilterPersistEnabled,
} from '@/lib/reportFilters';

/**
 * «Мои настройки» — персональные параметры интерфейса текущего пользователя.
 *
 * Все настройки этой страницы локальные (браузер/устройство): и запоминание
 * фильтров, и выбор видимых пунктов меню хранятся в localStorage. Страница
 * доступна любому залогиненному пользователю админ-панели (без отдельного
 * права).
 */
export default function UserSettingsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();

  // --- Отчёты: запоминать фильтры ---
  const [persistFilters, setPersistFilters] = useState(() => isReportFilterPersistEnabled());
  const [savedHint, setSavedHint] = useState('');

  const onTogglePersist = (checked) => {
    setPersistFilters(checked);
    setReportFilterPersistEnabled(checked);
    setSavedHint(checked
      ? 'Включено — отчёты будут запоминать фильтры.'
      : 'Выключено — сохранённые фильтры отчётов очищены.');
  };

  // --- Меню: какие пункты показывать ---
  // Только пункты, доступные текущему пользователю по правам — их и предлагаем
  // включать/выключать. Секции без доступных пунктов не показываем.
  const menuSections = useMemo(
    () => NAV_SECTIONS
      .map((sec) => ({ ...sec, items: sec.items.filter((it) => isNavItemVisible(it, can)) }))
      .filter((sec) => sec.items.length > 0),
    [can],
  );
  const [hidden, setHidden] = useState(() => new Set(getHiddenNavItems()));

  // --- Стартовая страница ---
  const [defaultPage, setDefaultPageState] = useState(() => getDefaultPage());
  // Доступные для выбора пути = разрешённые правами пункты меню. Если
  // сохранённое значение больше не разрешено — показываем «стандартную».
  const allowedPaths = useMemo(
    () => new Set(menuSections.flatMap((s) => s.items.map((i) => i.to))),
    [menuSections],
  );
  const defaultPageValue = allowedPaths.has(defaultPage) ? defaultPage : '';
  const onChangeDefaultPage = (value) => {
    setDefaultPageState(value);
    setDefaultPage(value);
  };

  const toggleItem = (id, show) => {
    const next = new Set(hidden);
    if (show) next.delete(id); else next.add(id);
    setHidden(next);
    setHiddenNavItems([...next]);
  };
  const showAll = () => {
    setHidden(new Set());
    setHiddenNavItems([]);
  };

  return (
    <>
      <Topbar
        breadcrumb={['Мои настройки']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {/* Запоминание фильтров в отчётах */}
        <div className="settings-card">
          <div className="settings-card__head">
            <h2 className="settings-card__title">Отчёты</h2>
            <p className="settings-card__sub">
              Персональные параметры интерфейса. Действуют в этом браузере
              на этом устройстве.
            </p>
          </div>

          <div style={{ padding: '16px 20px' }}>
            <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={persistFilters}
                onChange={(e) => onTogglePersist(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
              />
              <span>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--fg)' }}>
                  Сохранять настройки фильтров в отчётах
                </span>
                <span style={{
                  display: 'block', marginTop: 4,
                  fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.55,
                }}>
                  Когда включено, страницы отчётов запоминают выбранные фильтры
                  и подставляют их при следующем открытии. Значения хранятся
                  локально в этом браузере. Если открыть отчёт по ссылке с
                  явными фильтрами — приоритет у ссылки.
                  <br />
                  Сейчас работает для «Реестра договоров»; постепенно подключим
                  остальные отчёты.
                </span>
              </span>
            </label>

            {savedHint && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--green-700)' }}>
                {savedHint}
              </div>
            )}
          </div>
        </div>

        {/* Стартовая страница */}
        <div className="settings-card" style={{ marginTop: 20 }}>
          <div className="settings-card__head">
            <h2 className="settings-card__title">Стартовая страница</h2>
            <p className="settings-card__sub">
              Какую страницу открывать при входе в систему. Доступны только
              разделы, разрешённые вам по правам. Настройка действует в этом
              браузере.
            </p>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div className="settings-field" style={{ maxWidth: 360 }}>
              <label className="settings-field__label">Открывать при входе</label>
              <select
                className="settings-field__input"
                value={defaultPageValue}
                onChange={(e) => onChangeDefaultPage(e.target.value)}
              >
                <option value="">Стандартная (Ученики)</option>
                {menuSections.map((sec) => (
                  <optgroup key={sec.title} label={sec.title}>
                    {sec.items.map((it) => (
                      <option key={it.to} value={it.to}>{it.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Выбор видимых пунктов меню */}
        <div className="settings-card" style={{ marginTop: 20 }}>
          <div className="settings-card__head" style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
          }}>
            <div>
              <h2 className="settings-card__title">Пункты меню</h2>
              <p className="settings-card__sub">
                Отметьте пункты, которые хотите видеть в боковом меню. Снятые —
                скрываются (только в этом браузере). Показаны лишь разделы,
                доступные вам по правам.
              </p>
            </div>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={showAll}
              disabled={hidden.size === 0}
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Показать все
            </button>
          </div>

          <div style={{ padding: '4px 20px 18px' }}>
            {menuSections.map((sec) => (
              <div key={sec.title} style={{ marginTop: 14 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.04em', color: 'var(--fg-faint)', marginBottom: 6,
                }}>
                  {sec.title}
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                  gap: '4px 16px',
                }}>
                  {sec.items.map((it) => {
                    const visible = !hidden.has(it.id);
                    return (
                      <label key={it.id} style={{
                        display: 'flex', alignItems: 'center', gap: 9,
                        cursor: 'pointer', padding: '3px 0', fontSize: 13,
                      }}>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={(e) => toggleItem(it.id, e.target.checked)}
                          style={{ width: 15, height: 15, flexShrink: 0, cursor: 'pointer' }}
                        />
                        <span style={{ color: 'var(--fg-muted)', display: 'inline-flex', flexShrink: 0 }}>
                          <Icon name={it.icon} size={15} />
                        </span>
                        <span style={{ color: visible ? 'var(--fg)' : 'var(--fg-faint)' }}>
                          {it.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
